import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  DaemonSchema,
  DAEMON_WIZARD_STEPS,
  canActivateDaemon,
  type DaemonInput,
} from '@confluent/shared/schemas/daemon';
import { EntityWizardLayout } from '../../../components/entity/EntityWizardLayout.js';
import { FieldHelpPanel } from '../../../components/entity/FieldHelpPanel.js';
import { NavigateAwayWarning } from '../../../components/entity/NavigateAwayWarning.js';
import { useDirtyGuard } from '../../../hooks/useDirtyGuard.js';
import { useEntityDraft } from '../../../hooks/useEntityDraft.js';
import { useWizardState } from '../../../hooks/useWizardState.js';
import { DAEMON_FIELD_HELP } from '../../../help/daemon-fields.js';
import { EntityAssistantDrawer } from '../../../components/assistant/EntityAssistantDrawer.js';
import { buildDaemonScope } from './DaemonScopeContext.js';
import { type DaemonScope, daemonWrites } from './daemon-queries.js';
import { HireStepIdentity } from './HireStepIdentity.js';
import { HireStepQualifications } from './HireStepQualifications.js';
import { HireStepWorkflows } from './HireStepWorkflows.js';
import { HireStepTriggers } from './HireStepTriggers.js';
import { HireStepSupervision } from './HireStepSupervision.js';
import type { WizardState } from './hire-types.js';
import { useToast } from '../../../components/ui';

interface NewDaemonPageProps {
  scope: DaemonScope;
}

// Starting form state. `department` is intentionally unset so the select lands
// on the placeholder option — Zod enforces selection before save. Cast to
// Partial<DaemonInput> because the schema requires department but the UI lets
// the user pick it.
const EMPTY = {
  name: '',
  type: 'operative' as const,
  mandate: '',
  templateId: null,
  permissionTier: 'observer' as const,
  requiresApproval: true,
  workflowDefinitionId: null,
  triggers: [],
  supervisorDaemonId: null,
  probationDays: null,
  lifecycleStatus: 'draft' as const,
} satisfies Partial<DaemonInput>;

/**
 * Dedicated wizard page for hiring an employee. Five logical steps per
 * ENTITY_CREATION_WIZARD_SPEC.md §12 (pilot). RHF + zod resolver drives
 * per-step validation (field lists, one schema — §3.3). Draft state lives
 * in localStorage (§13.1). Assistant tab is a placeholder until the
 * WorkforceAssistant wrapper ships (Batch 6).
 */
export const NewDaemonPage: React.FC<NewDaemonPageProps> = ({ scope }) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [searchParams] = useSearchParams();
  // Stable per mount — fresh conversation for each new-daemon session.
  const draftKey = useMemo(() => crypto.randomUUID(), []);
  const { basePath } = buildDaemonScope(scope);
  const marketplaceListingId = searchParams.get('listingId');
  // What the assistant is told beside the form: whose catalog it reads, and the listing this draft was started from.
  const assistantExtras = useMemo(
    () => ({
      scope,
      ...(marketplaceListingId && /^\d+$/.test(marketplaceListingId)
        ? { marketplaceListingId: Number(marketplaceListingId) }
        : {}),
    }),
    [scope, marketplaceListingId],
  );

  const { draft, setDraft, clearDraft } = useEntityDraft<Record<string, unknown>>('daemon', 'new', EMPTY);

  const form = useForm<DaemonInput>({
    resolver: zodResolver(DaemonSchema),
    defaultValues: draft as Partial<DaemonInput>,
    mode: 'onBlur',
  });

  // Reactive live values — any RHF setValue / user edit re-renders the page so
  // the Activate gate + canAdvance flags stay fresh. (form.getValues() alone is
  // NOT reactive and would leave the buttons stale.)
  const liveValues = useWatch({ control: form.control }) as Partial<DaemonInput>;
  const activationCheck = canActivateDaemon(liveValues as DaemonInput);

  // Keep the draft in localStorage in sync with every RHF update. Use the
  // subscription form of watch() — the reactive form (`form.watch()` without
  // callback) returns a new object ref on every render, which combined with
  // a useEffect dep and a setState body produces an infinite setState-in-
  // useEffect loop.
  useEffect(() => {
    const subscription = form.watch((value) => {
      setDraft(value as DaemonInput);
    });
    return () => subscription.unsubscribe();
  }, [form, setDraft]);

  const wizard = useWizardState<DaemonInput>({
    steps: DAEMON_WIZARD_STEPS,
    form,
    canActivate: canActivateDaemon,
  });

  const isDirty = form.formState.isDirty;
  const dirtyGuard = useDirtyGuard(isDirty);

  // Chosen in the Qualifications step; null = the workforce default, resolved by the server.
  const [runtimePolicyId, setRuntimePolicyId] = useState<number | null>(null);

  const createMutation = useMutation({
    ...daemonWrites(scope).create(),
    onSuccess: (created) => {
      clearDraft();
      navigate(created.id ? `${basePath}/${created.id}` : basePath);
    },
    onError: (err: Error) => notify('Save failed: ' + err.message, 'danger'),
  });
  const createDaemon = (input: DaemonInput) =>
    createMutation.mutate({ body: { ...input, ...(runtimePolicyId !== null ? { runtimePolicyId } : {}) } });

  const handleSaveDraft = async () => {
    const ok = await form.trigger(['name']); // minimum required
    if (!ok) return;
    createDaemon({ ...form.getValues(), lifecycleStatus: 'draft' });
  };

  const handleActivate = async () => {
    const ok = await form.trigger();
    if (!ok) return;
    const check = canActivateDaemon(form.getValues());
    if (!check.ok) {
      notify('Missing required fields for activation: ' + check.missing.join(', '), 'danger');
      return;
    }
    createDaemon({ ...form.getValues(), lifecycleStatus: 'active' });
  };

  const stepFields = wizard.step.fields as ReadonlyArray<string>;
  const errorFields = new Set(Object.keys(form.formState.errors).filter((k) => (stepFields as string[]).includes(k)));

  // Step-specific form sections — each reuses the existing HireStep* component.
  // The step components take a legacy WizardState shape; adapt via values + onChange.
  const formState = form.watch() as unknown as WizardState;
  const handleChange = (updates: Partial<WizardState>) => {
    // RHF: when a field currently has an error, re-run the resolver on set so the
    // error clears the moment the value becomes valid. When the field is clean,
    // skip validation to avoid spamming errors on first keystroke (initial
    // validation still happens via wizard.next()/handleActivate → form.trigger()).
    const errors = form.formState.errors;
    for (const [k, v] of Object.entries(updates)) {
      form.setValue(k as keyof DaemonInput, v as never, {
        shouldDirty: true,
        shouldValidate: k in errors,
      });
    }
  };

  const currentStepBody = (() => {
    switch (wizard.step.key) {
      case 'identity':
        return <HireStepIdentity state={formState} onChange={handleChange} />;
      case 'qualifications':
        return (
          <HireStepQualifications
            state={formState}
            onChange={handleChange}
            runtimePolicyId={runtimePolicyId}
            onRuntimePolicyChange={setRuntimePolicyId}
          />
        );
      case 'workflows':
        return <HireStepWorkflows state={formState} onChange={handleChange} />;
      case 'triggers':
        return <HireStepTriggers state={formState} onChange={handleChange} />;
      case 'supervision':
        return <HireStepSupervision state={formState} onChange={handleChange} />;
      default:
        return null;
    }
  })();

  return (
    <>
      <FormProvider {...form}>
        <EntityWizardLayout
          entityLabel={scope === 'system' ? 'Daemon' : 'Employee'}
          backTo={basePath}
          steps={DAEMON_WIZARD_STEPS.map((s: { key: string; label: string }) => ({ key: s.key, label: s.label }))}
          currentStep={wizard.currentStep}
          furthestReached={wizard.currentStep}
          onRequestStep={(i) => {
            void wizard.goToStep(i);
          }}
          onPrevious={wizard.previous}
          onNext={() => {
            void wizard.next();
          }}
          onSaveDraft={handleSaveDraft}
          onActivate={handleActivate}
          canAdvance
          canSaveDraft={!!liveValues.name?.trim()}
          canActivate={activationCheck.ok}
          isSaving={createMutation.isPending}
          sidebarStorageKey="daemon"
          marketplaceBanner={
            marketplaceListingId ? (
              <p className="text-xs text-muted mt-0.5">
                Customizing marketplace listing{' '}
                <span className="font-mono text-accent-text">#{marketplaceListingId}</span>
              </p>
            ) : undefined
          }
          formSlot={
            <section className="bg-surface rounded-lg border p-6">
              <h2 className="text-sm font-semibold text-heading mb-4 uppercase tracking-wider">{wizard.step.label}</h2>
              {currentStepBody}
              {Object.keys(form.formState.errors).filter((k) => (stepFields as string[]).includes(k)).length > 0 && (
                <div className="mt-4 text-xs text-danger">Fix the highlighted fields before continuing.</div>
              )}
            </section>
          }
          helpSlot={
            <FieldHelpPanel
              stepLabel={wizard.step.label}
              stepIndex={wizard.currentStep}
              totalSteps={DAEMON_WIZARD_STEPS.length}
              fieldKeys={stepFields}
              helpMap={DAEMON_FIELD_HELP}
              fieldsWithErrors={errorFields}
            />
          }
          navigateAwayWarning={
            <NavigateAwayWarning
              open={dirtyGuard.pendingNavigation}
              onStay={dirtyGuard.cancelLeave}
              onLeave={dirtyGuard.confirmLeave}
            />
          }
        />
      </FormProvider>

      {/* EntityAssistantDrawer is a sibling of FormProvider (spec §3.1).
        onApplyFieldUpdate maps partial DaemonInput keys back via RHF setValue
        (mirrors the pre-57 WorkforceAssistant handleChange pattern). */}
      <EntityAssistantDrawer
        subjectType="daemon-draft"
        draftKey={draftKey}
        entryPoint="workforce"
        currentFormValues={liveValues as unknown as Record<string, unknown>}
        currentStepKey={wizard.step.key}
        contextExtras={assistantExtras}
        onApplyFieldUpdate={(partial) => {
          for (const [k, v] of Object.entries(partial)) {
            form.setValue(k as keyof DaemonInput, v as never, { shouldDirty: true });
          }
        }}
      />
    </>
  );
};
